import type { LocalTrack, RemoteTrack } from './jitsi-api';
import type {
  JitsiLocalTrackLike,
  JitsiRemoteTrackLike,
} from '../../../types/lib-jitsi-meet';
import { JitsiTrackCreationError } from './jitsi-errors';

export class LocalTrackAdapter implements LocalTrack {
  constructor(private readonly track: JitsiLocalTrackLike) {}

  /** Escape hatch for `replaceTrack`, which needs the library's own object. */
  unwrap(): JitsiLocalTrackLike {
    return this.track;
  }

  getType(): 'audio' | 'video' {
    return this.track.getType();
  }

  getTrack(): MediaStreamTrack {
    return this.track.getTrack();
  }

  getDeviceId(): string {
    return this.track.getDeviceId();
  }

  isMuted(): boolean {
    return this.track.isMuted();
  }

  mute(): Promise<void> {
    return this.track.mute();
  }

  unmute(): Promise<void> {
    return this.track.unmute();
  }

  dispose(): Promise<void> {
    return this.track.dispose();
  }

  attach(element: HTMLMediaElement): void {
    this.track.attach(element);
  }

  detach(element: HTMLMediaElement): void {
    this.track.detach(element);
  }
}

/**
 * A track published by the other participant.
 *
 * Narrower than `LocalTrack` because a remote track is only ever rendered — the
 * page cannot mute or dispose someone else's media, and modelling that
 * distinction in the type keeps the difference from being forgotten.
 */
export class RemoteTrackAdapter implements RemoteTrack {
  constructor(private readonly track: JitsiRemoteTrackLike) {}

  getType(): 'audio' | 'video' {
    return this.track.getType();
  }

  getParticipantId(): string {
    return this.track.getParticipantId();
  }

  isMuted(): boolean {
    return this.track.isMuted();
  }

  attach(element: HTMLMediaElement): void {
    this.track.attach(element);
  }

  detach(element: HTMLMediaElement): void {
    this.track.detach(element);
  }
}


/** Recover the library's own object, which `replaceTrack` and `addTrack` need. */
export function unwrapTrack(track: LocalTrack): JitsiLocalTrackLike {
  if (track instanceof LocalTrackAdapter) {
    return track.unwrap();
  }
  throw new JitsiTrackCreationError(
    'track did not originate from this adapter and cannot be passed to lib-jitsi-meet',
  );
}
